import { NgOptimizedImage } from '@angular/common';
import { Component, inject, input, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from 'app/core/services/auth/auth.service';
import { DnsRedirectGuard } from 'app/core/guards/dns-redirect/dns-redirect.guard';
import { NormalizeService } from 'app/shared/utils/normalize.service';
import { UnsubscribeOnDestroyAdapter } from 'app/shared/utils/unsubscribeOnDestroyAdapter';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { environment } from '../../../../environments/environment';
import { LucideModule } from 'app/shared/modules/lucide.module';
import { finalize } from 'rxjs';

/** O login é sempre por documento: 11 dígitos para CPF, 14 para CNPJ. */
const DIGITOS_DOCUMENTO = [11, 14];

const ROTA_POS_LOGIN = '/area-cidadao';


@Component({
  selector: 'app-login-card',
  templateUrl: './login-card.component.html',
  styleUrls: ['./login-card.component.css'],
  imports: [ReactiveFormsModule, NgxMaskDirective, RouterLink, NgOptimizedImage, LucideModule],
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class LoginCardComponent extends UnsubscribeOnDestroyAdapter {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly dnsRedirectGuard = inject(DnsRedirectGuard);
  private readonly normalizeService = inject(NormalizeService);

  readonly destino = input('');

  readonly nivelTitulo = input(2);

  readonly authForm = this.formBuilder.group({
    usuario: ['', [Validators.required, LoginCardComponent.validarUsuario]],
    senha: ['', Validators.required],
  });

  readonly loading = signal(false);
  readonly loginError = signal('');
  readonly senhaVisivel = signal(false);
  /** Máscara nativa do ngx-mask: alterna entre CPF e CNPJ conforme os dígitos digitados. */
  readonly mascaraDocumento = 'CPF_CNPJ';

  get f() {
    return this.authForm.controls;
  }

  alternarSenha(): void {
    this.senhaVisivel.update((visivel) => !visivel);
  }

  onSubmit(): void {
    if (this.authForm.invalid) {
      this.authForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.loginError.set('');

    const informado = String(this.f.usuario.value ?? '').trim();
    const usuario = this.normalizeService.cpfCnpj(informado);

    this.subs.sink = this.authService
      .login(usuario, this.f.senha.value ?? '')
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (res) {
            this.router.navigateByUrl(
              this.destino() || this.dnsRedirectGuard.getRedirectPath(ROTA_POS_LOGIN)
            );
          }
        },
        error: (error) => {
          const errors = Array.isArray(error.error?.errors)
            ? error.error.errors.filter(Boolean).join(' ')
            : '';

          const message =
            error.error?.message ||
            errors ||
            'Não foi possível entrar. Verifique os dados e tente novamente.';

          const detalhe = !environment.production && errors && errors !== message ? ` (${errors})` : '';

          this.loginError.set(`${error.status}: ${message}${detalhe}`);
        },
      });
  }

  private static validarUsuario(control: AbstractControl): ValidationErrors | null {
    const digitos = String(control.value ?? '').replace(/\D/g, '');

    if (!digitos) {
      return null;
    }

    return DIGITOS_DOCUMENTO.includes(digitos.length) ? null : { usuarioInvalido: true };
  }
}
